import { wencesApi } from "@/api/wencesApi";
import { logoutAction, loadSystemCurrency } from "@/pages/Auth/actions/login.action";
import {
  fetchTranslationOverridesFromApiAndPersist,
  syncI18nLocaleFromUser,
} from "@/i18n";
import { markSessionAlive } from "@/utils/browserCloseLogout";

export const EMAIL_SCOPED_FLAG = "emailScopedSession";
export const EMAIL_SCOPED_PATH_KEY = "emailScopedPath";

export function hasNormalAppSession() {
  const token = localStorage.getItem("token");
  if (!token || token.length < 10) return false;
  return localStorage.getItem(EMAIL_SCOPED_FLAG) !== "1";
}

export function isEmailScopedSession() {
  return localStorage.getItem(EMAIL_SCOPED_FLAG) === "1";
}

export function getEmailScopedPath() {
  return localStorage.getItem(EMAIL_SCOPED_PATH_KEY) || "";
}

export function normalizeRouteScope(fullPath) {
  const raw = String(fullPath || "").trim() || "/";
  try {
    if (raw.startsWith("http")) {
      const u = new URL(raw);
      if (u.hash && u.hash.startsWith("#/")) {
        const h = u.hash.slice(1);
        return h.startsWith("/") ? h : `/${h}`;
      }
      return `${u.pathname || "/"}${u.search || ""}`;
    }
  } catch (_) {
    /* keep */
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
}

export function pathsMatchScopedRoute(currentFullPath, scopedPath) {
  const a = normalizeRouteScope(currentFullPath);
  const b = normalizeRouteScope(scopedPath);
  return a === b;
}

function persistScopedLogin(data) {
  const { token, user, role, scopedPath } = data;
  localStorage.setItem("token", token);
  localStorage.setItem("userId", user._id);
  markSessionAlive();
  localStorage.setItem("_id", user._id);
  localStorage.setItem("role", role);
  localStorage.setItem("name", user.name);
  if (user.restaurant) localStorage.setItem("restaurant", user.restaurant);
  if (user.country) localStorage.setItem("country", user.country);
  localStorage.setItem(EMAIL_SCOPED_FLAG, "1");
  localStorage.setItem(EMAIL_SCOPED_PATH_KEY, normalizeRouteScope(scopedPath));
  syncI18nLocaleFromUser(user);
}

export async function exchangeEmailAccessToken(eal) {
  const { data } = await wencesApi.post("/api/public/email-access/exchange", { eal });
  if (!data?.ok || !data?.data?.token) {
    throw new Error(data?.error || "Enlace inválido");
  }
  return data.data;
}

/**
 * Si hay ?eal= y no hay sesión normal previa, intercambia token y deja sesión acotada.
 * @returns {Promise<{ replaced?: object }|null>}
 */
export async function tryConsumeEmailAccessLink(route) {
  const eal = route?.query?.eal;
  if (!eal) return null;

  if (hasNormalAppSession()) {
    return { stripOnly: true };
  }

  const data = await exchangeEmailAccessToken(eal);
  persistScopedLogin(data);
  await loadSystemCurrency(data.token);
  await fetchTranslationOverridesFromApiAndPersist();

  const scopedPath = normalizeRouteScope(data.scopedPath || route.fullPath);
  const nextQuery = { ...route.query };
  delete nextQuery.eal;

  return {
    replace: {
      path: scopedPath.split("?")[0] || route.path,
      query: mergeQueryFromScopedPath(scopedPath, nextQuery),
    },
  };
}

function mergeQueryFromScopedPath(scopedPath, baseQuery) {
  const qIndex = scopedPath.indexOf("?");
  if (qIndex < 0) return baseQuery;
  const params = new URLSearchParams(scopedPath.slice(qIndex + 1));
  const out = { ...baseQuery };
  params.forEach((val, key) => {
    if (key !== "eal") out[key] = val;
  });
  return out;
}

/**
 * @returns {boolean} true si se abortó la navegación (logout)
 */
export async function enforceEmailScopedNavigation(to, from, next) {
  if (!isEmailScopedSession()) return false;

  const scoped = getEmailScopedPath();
  if (!scoped) return false;

  if (pathsMatchScopedRoute(to.fullPath, scoped)) {
    return false;
  }

  await logoutAction();
  localStorage.removeItem(EMAIL_SCOPED_FLAG);
  localStorage.removeItem(EMAIL_SCOPED_PATH_KEY);
  next({ path: "/login", query: { reason: "email_scope_ended" } });
  return true;
}

export function clearEmailScopedMarkers() {
  localStorage.removeItem(EMAIL_SCOPED_FLAG);
  localStorage.removeItem(EMAIL_SCOPED_PATH_KEY);
}
